import type { Block } from '@pageboss/shared';
import { InspectorSection } from './inspector-section';
import { InspectorField } from './inspector-field';
import { NumberField } from './number-field';
import { Repeater, RepeaterTitle } from './repeater';
import { SocialPlatformPicker } from './social-platform-picker';

type FooterBlock = Extract<Block, { type: 'footer' }>;
type FooterProps = FooterBlock['props'];
type Link = FooterProps['links'][number];
type SocialLink = FooterProps['socialLinks'][number];
type Platform = SocialLink['platform'];

function newLink(): Link {
  return { label: 'New link', href: '' };
}

function newSocialLink(): SocialLink {
  return { platform: 'instagram', href: '' };
}

interface FooterInspectorProps {
  block: FooterBlock;
  onChange: (next: FooterProps) => void;
}

export function FooterInspector({ block, onChange }: FooterInspectorProps) {
  const props = block.props;
  const patch = (next: Partial<FooterProps>) => {
    const merged: FooterProps = { ...props, ...next };
    if (next.copyrightYear === undefined && 'copyrightYear' in next) {
      delete merged.copyrightYear;
    }
    onChange(merged);
  };

  return (
    <>
      <InspectorSection>
        <div className="flex flex-col gap-4">
          <NumberField
            label="Copyright year (optional)"
            value={props.copyrightYear}
            placeholder="2026"
            helperText="Defaults to the current year if left blank"
            min={1900}
            max={2100}
            onChange={(v) => patch({ copyrightYear: v })}
          />
          <p className="text-xs text-text-tertiary -mt-2">
            Edit business name and tagline directly on the canvas.
          </p>
        </div>
      </InspectorSection>

      <InspectorSection label="Links">
        <Repeater
          items={props.links}
          onChange={(links) => patch({ links: links as FooterProps['links'] })}
          addLabel="Add link"
          makeNew={newLink}
          preventEmpty={false}
          renderTitle={(link) => <RepeaterTitle>{link.label}</RepeaterTitle>}
          renderForm={(link, _i, update) => (
            <>
              <InspectorField
                label="Label"
                value={link.label}
                placeholder="Privacy policy"
                onChange={(v) => update({ label: v })}
              />
              <InspectorField
                label="Link"
                type="url"
                value={link.href}
                placeholder="https://... or /privacy"
                onChange={(v) => update({ href: v })}
              />
            </>
          )}
        />
      </InspectorSection>

      <InspectorSection label="Social links">
        <Repeater
          items={props.socialLinks}
          onChange={(socialLinks) =>
            patch({ socialLinks: socialLinks as FooterProps['socialLinks'] })
          }
          addLabel="Add social link"
          makeNew={newSocialLink}
          preventEmpty={false}
          renderTitle={(item) => (
            <span className="capitalize">{item.platform}</span>
          )}
          renderForm={(item, _i, update) => (
            <>
              <SocialPlatformPicker
                value={item.platform}
                onChange={(platform: Platform) => update({ platform })}
              />
              <InspectorField
                label="URL"
                type="url"
                value={item.href}
                placeholder="https://instagram.com/yourhandle"
                onChange={(v) => update({ href: v })}
              />
            </>
          )}
        />
      </InspectorSection>
    </>
  );
}
